import type { SubmitHandler } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { queryString } from '@olivierpascal/helpers';
import { Button, Stack, Step } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { IApiAppPlatform } from '~/lib/api/modules/Apps';
import type { IPackagePreparerStepProps } from './PackagePreparerStep';
import { FormField } from '~/components/atoms/FormField';
import { Link } from '~/components/atoms/Link';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { OrganizationSelector } from '~/components/organisms/fields/OrganizationSelector';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppMutation } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';
import { IApiGetOrganizationResponseBody } from '~/lib/api/modules/Organizations';
import { useOrganizationAttachAppMutation } from '~/lib/api/modules/Organizations/hooks/useOrganizationAttachAppMutation';
import { useOrganizationQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationQuery';

export type IPackagePreparerOrganizationStepProps = IPackagePreparerStepProps;

export type IPackagePreparerOrganizationStepFormValue = {
  organizationId?: string;
};

const styles = stylex.create({
  field: {
    width: 400,
  },
});

const checkIsOrganizationConfigured = (
  organization: IApiGetOrganizationResponseBody,
  platform: IApiAppPlatform,
): boolean => (platform === 'ios' ? !!organization.apple.teamId?.length : true);

export const PackagePreparerOrganizationStep: React.FC<
  IPackagePreparerOrganizationStepProps
> = (props) => {
  const { appId, onSuccess, platform, ...other } = props;
  const { t } = useTranslation();
  const form = useForm<IPackagePreparerOrganizationStepFormValue>();
  const appQuery = useAppQuery({ appId });
  const location = useLocation();
  const app = appQuery.data;
  const organizationAttachAppMutation = useOrganizationAttachAppMutation();
  const appUpdateMutation = useUpdateAppMutation();

  const organizationQuery = useOrganizationQuery(
    app?.organization ? { organizationId: app.organization._id } : undefined,
  );
  const organization = organizationQuery.data;
  const isOrganizationSetUp = organization
    ? checkIsOrganizationConfigured(organization, platform)
    : undefined;

  const selectedOrganizationId = form.watch('organizationId');
  const selectedOrganizationQuery = useOrganizationQuery(
    selectedOrganizationId
      ? { organizationId: selectedOrganizationId }
      : undefined,
  );
  const selectedOrganization = selectedOrganizationQuery.data;
  const isSelectedOrganizationSetUp = selectedOrganization
    ? checkIsOrganizationConfigured(selectedOrganization, platform)
    : undefined;
  const metadataUpdateRequired =
    app &&
    selectedOrganization &&
    (app.organization?._id !== selectedOrganization._id ||
      (organization &&
        (platform === 'ios' && !!app.builds?.ios
          ? app.builds.ios.author !== organization.apple.companyName ||
            app.builds.ios.email !== organization.email
          : platform === 'android' && !!app.builds?.android
            ? app.builds.android.author !== organization.name ||
              app.builds.android.email !== organization.email
            : false)));
  const submitting =
    organizationAttachAppMutation.isLoading || appUpdateMutation.isLoading;

  const isInitRef = useRef(false);
  const completed = isOrganizationSetUp === true && !metadataUpdateRequired;

  useEffect(() => {
    if (!organization || !app || isInitRef.current) {
      return;
    }
    isInitRef.current = true;

    if (completed) {
      onSuccess?.({
        source: 'init',
      });
    }
  }, [organization, app, completed, onSuccess]);

  const handleSubmit: SubmitHandler<
    IPackagePreparerOrganizationStepFormValue
  > = async (values) => {
    if (submitting) {
      return;
    }

    if (metadataUpdateRequired && organization) {
      await appUpdateMutation.mutateAsync({
        appId,
        body:
          platform === 'ios'
            ? {
                iosAuthor: organization.apple.companyName,
                iosEmail: organization.email,
              }
            : platform === 'android'
              ? {
                  androidAuthor: organization.name,
                  androidEmail: organization.email,
                }
              : {},
      });
    }

    if (values.organizationId === app?.organization?._id) {
      onSuccess?.({
        source: 'user',
      });

      return;
    }

    return organizationAttachAppMutation.mutateAsync(
      {
        organizationId: values.organizationId!,
        appId,
      },
      {
        onSuccess: onSuccess ? () => onSuccess({ source: 'user' }) : undefined,
      },
    );
  };

  const appStoreName =
    platform === 'ios'
      ? t('PackagePreparerOrganizationStep.store.ios')
      : t('PackagePreparerOrganizationStep.store.android');
  const theAppStoreName =
    platform === 'ios'
      ? t('PackagePreparerOrganizationStep.the-store.ios')
      : t('PackagePreparerOrganizationStep.the-store.android');

  return (
    <Step
      {...other}
      label={t('PackagePreparerOrganizationStep.name')}
      alwaysExpanded={!other.disabled}
    >
      {appQuery.isError ? (
        <ErrorMessage error={appQuery.error} />
      ) : appQuery.isLoading ? (
        t('common.loading')
      ) : app ? (
        ({ active }) =>
          active ? (
            <Stack
              as="form"
              gap={6}
              align="start"
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <Trans
                parent="p"
                i18nKey="PackagePreparerOrganizationStep.description"
                values={{
                  store: appStoreName,
                  'the-store': theAppStoreName,
                }}
              />
              <FormField
                name="organizationId"
                control={form.control}
                as={OrganizationSelector}
                sx={styles.field}
                variant="outlined"
                defaultValue={app.organization?._id}
                rules={{
                  required: true,
                  validate: () => isSelectedOrganizationSetUp,
                }}
                hasError={
                  isSelectedOrganizationSetUp === false ? true : undefined
                }
                errorText={
                  isSelectedOrganizationSetUp === false
                    ? t(
                        'PackagePreparerOrganizationStep.error.organization-not-set-up',
                        {
                          store: appStoreName,
                          'the-store': theAppStoreName,
                        },
                      )
                    : undefined
                }
                canCreate
                createUrl={`/organizations/create?${queryString.build({
                  nextUrl: location.pathname,
                  cancelUrl: location.pathname,
                })}`}
              />
              <Stack horizontal gap={2}>
                <Button
                  type="submit"
                  loading={selectedOrganizationQuery.isLoading || submitting}
                >
                  {t('common.next')}
                </Button>
                {isSelectedOrganizationSetUp === false ? (
                  <Button
                    loading={submitting}
                    href={`/organizations/${form.watch('organizationId')}/settings?${queryString.build(
                      {
                        nextUrl: location.pathname,
                        cancelUrl: location.pathname,
                      },
                    )}`}
                    variant="outlined"
                  >
                    {t('PackagePreparerOrganizationStep.setup-organization')}
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          ) : organizationQuery.data ? (
            <Stack gap={2} align="start">
              <div>
                {t('PackagePreparerOrganizationStep.summary', {
                  store: appStoreName,
                  'the-store': theAppStoreName,
                })}
              </div>
              <div>
                <Trans
                  parent="div"
                  i18nKey="PackagePreparerOrganizationStep.summary-field.account-name"
                  values={{ value: organizationQuery.data.name }}
                />
                <Trans
                  parent="div"
                  i18nKey="PackagePreparerOrganizationStep.summary-field.contact-email-address"
                  values={{
                    value: organizationQuery.data.email ?? t('common.n-a'),
                  }}
                />
                {platform === 'ios' ? (
                  <Trans
                    parent="div"
                    i18nKey="PackagePreparerOrganizationStep.summary-field.apple-team-id"
                    values={{
                      value:
                        organizationQuery.data.apple.teamId ?? t('common.n-a'),
                    }}
                  />
                ) : null}
              </div>
              {isOrganizationSetUp ? null : (
                <Trans
                  parent="div"
                  i18nKey="PackagePreparerOrganizationStep.error.configure-organization"
                  values={{
                    store: appStoreName,
                    'the-store': theAppStoreName,
                  }}
                  components={{
                    a: (
                      <Link
                        to={`/organizations/${organizationQuery.data?._id}/settings`}
                      />
                    ),
                  }}
                />
              )}
            </Stack>
          ) : null
      ) : null}
    </Step>
  );
};

PackagePreparerOrganizationStep.displayName = 'Step';
